import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

const TEST_STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const TestsForm = ({ testsOrdered = [], setTestsOrdered }) => {
  const [newTest, setNewTest] = useState({ 
    test_name: '', 
    testName: '', // For backward compatibility
    instructions: '', 
    status: 'Pending' 
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [showError, setShowError] = useState(false);

  // Normalize tests prop to handle different formats
  const normalizedTests = useMemo(() => {
    if (!testsOrdered || testsOrdered.length === 0) return [];
    return testsOrdered.map(t => ({
      test_name: t.test_name || t.testName || '',
      testName: t.testName || t.test_name || '', // Keep both for compatibility
      instructions: t.instructions || '',
      status: t.status || 'Pending',
    }));
  }, [testsOrdered]);

  const validateTest = useCallback((test) => {
    const newErrors = {};
    
    if (!test.test_name || test.test_name.trim() === '') {
      newErrors.test_name = 'Test name is required';
    }
    
    return newErrors;
  }, []);

  const handleFieldChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setNewTest(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'test_name' ? { testName: value } : {}), // Sync both fields
      ...(field === 'testName' ? { test_name: value } : {}), // Sync both fields
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const addOrUpdateTest = useCallback(() => {
    const validationErrors = validateTest(newTest);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowError(true);
      return;
    }

    const testToAdd = {
      test_name: newTest.test_name.trim() || newTest.testName.trim(),
      testName: newTest.testName.trim() || newTest.test_name.trim(), // Keep both
      instructions: newTest.instructions.trim(),
      status: newTest.status || 'Pending',
    };

    if (editingIndex !== null) {
      const updatedTests = [...normalizedTests];
      updatedTests[editingIndex] = testToAdd;
      setTestsOrdered(updatedTests);
      setEditingIndex(null);
    } else {
      setTestsOrdered([...normalizedTests, testToAdd]);
    }

    // Reset form
    setNewTest({ 
      test_name: '', 
      testName: '',
      instructions: '', 
      status: 'Pending' 
    });
    setErrors({});
    setShowError(false);
  }, [newTest, editingIndex, normalizedTests, setTestsOrdered, validateTest]);

  const editTest = useCallback((index) => {
    const test = normalizedTests[index];
    setNewTest({
      test_name: test.test_name || test.testName || '',
      testName: test.testName || test.test_name || '',
      instructions: test.instructions || '',
      status: test.status || 'Pending',
    });
    setEditingIndex(index);
    setErrors({});
    setShowError(false);
  }, [normalizedTests]);

  const deleteTest = useCallback((index) => {
    const updatedTests = normalizedTests.filter((_, i) => i !== index);
    setTestsOrdered(updatedTests);
    
    // Reset editing if deleting the item being edited
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewTest({ 
        test_name: '', 
        testName: '',
        instructions: '', 
        status: 'Pending' 
      });
      setErrors({});
      setShowError(false);
    }
  }, [normalizedTests, setTestsOrdered, editingIndex]);

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    setNewTest({ 
      test_name: '', 
      testName: '',
      instructions: '', 
      status: 'Pending' 
    });
    setErrors({});
    setShowError(false);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'Cancelled': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h6'>Tests Ordered</Typography>
        {normalizedTests.length > 0 && (
          <Chip 
            label={`${normalizedTests.length} test${normalizedTests.length !== 1 ? 's' : ''}`} 
            color="primary" 
            size="small" 
          />
        )}
      </Box>

      {showError && Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowError(false)}>
          Please fix the following errors:
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {Object.values(errors).filter(Boolean).map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <TextField
        fullWidth
        label='Test Name'
        placeholder='E.g., Complete Blood Count, X-Ray Chest, ECG'
        required
        value={newTest.test_name || newTest.testName}
        onChange={handleFieldChange('test_name')}
        error={!!errors.test_name}
        helperText={errors.test_name || 'Enter the name of the test to be ordered'}
        sx={{ marginBottom: 2 }}
      />
      
      <TextField
        fullWidth
        label='Instructions'
        placeholder='Special instructions for the test (optional)'
        multiline
        rows={2}
        value={newTest.instructions}
        onChange={handleFieldChange('instructions')}
        helperText="Any special instructions or requirements for the test"
        sx={{ marginBottom: 2 }}
      />

      <FormControl fullWidth sx={{ marginBottom: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={newTest.status}
          label="Status"
          onChange={handleFieldChange('status')}
        >
          {TEST_STATUS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant='contained'
          color='primary'
          startIcon={editingIndex !== null ? <EditIcon /> : <AddIcon />}
          onClick={addOrUpdateTest}
        >
          {editingIndex !== null ? 'Update Test' : 'Add Test'}
        </Button>
        {editingIndex !== null && (
          <Button
            variant='outlined'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </Box>

      {normalizedTests.length > 0 ? (
        <Box sx={{ marginTop: 3 }}>
          {normalizedTests.map((test, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                padding: 2,
                marginBottom: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: editingIndex === index ? '2px solid' : '1px solid',
                borderColor: editingIndex === index ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                    {test.test_name || test.testName || 'Unnamed Test'}
                  </Typography>
                  <Chip 
                    label={test.status} 
                    color={getStatusColor(test.status)}
                    size="small"
                  />
                </Box>
                {test.instructions && (
                  <Typography variant='body2' color="text.secondary">
                    <strong>Instructions:</strong> {test.instructions}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  color='primary'
                  size="small"
                  onClick={() => editTest(index)}
                  disabled={editingIndex !== null && editingIndex !== index}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color='error'
                  size="small"
                  onClick={() => deleteTest(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant='body2' color="text.secondary">
            No tests ordered yet. Add a test above.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TestsForm;
